import React, { useMemo, useState, useEffect } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend
} from "chart.js";
import styled from "styled-components";
import { Info } from "lucide-react";
import { Tooltip as InfoTooltip } from "react-tooltip";

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);

// Keep only these three categories
const COLORS = {
  Kraj: "#2e7d32",      // green
  UE: "#fb8c00",        // orange
  Eksport: "#1976d2",   // blue
};

const safeParseValue = (value) => {
  const num = parseFloat(value);
  return isNaN(num) || num < 0 ? 0 : num;
};

const formatNumber = (value) =>
  value.toLocaleString("pl-PL", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const months = [
  "Styczeń", "Luty", "Marzec", "Kwiecień", "Maj", "Czerwiec",
  "Lipiec", "Sierpień", "Wrzesień", "Październik", "Listopad", "Grudzień"
];

// === Styled Components ===
const ChartTitle = styled.div`
  font-family: Arial, sans-serif;
  font-size: 12px;
  font-style: normal;
  font-weight: bold;
  color: #666;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const InfoIconWrapper = styled.div`
  cursor: pointer;
  color: #666;
  transition: color 0.3s;
  &:hover { color: #000; }
  svg { width: 18px; height: 18px; }
`;

const TransactionStructureOverTime = ({ data }) => {
  const years = useMemo(() => {
    const yearSet = new Set();
    data.forEach((row) => {
      const date = new Date(row.DataWystawienia);
      if (!isNaN(date)) yearSet.add(date.getFullYear());
    });
    return Array.from(yearSet).sort((a, b) => b - a);
  }, [data]);

  const [selectedYear, setSelectedYear] = useState(null);

  useEffect(() => {
    if (years.length > 0 && !selectedYear) {
      setSelectedYear(years[0]);
    }
  }, [years, selectedYear]);

  const monthlyTotals = useMemo(() => {
    if (!selectedYear) return [];

    const totals = Array(12).fill(null).map(() => ({
      Kraj: 0,
      UE: 0,
      Eksport: 0,
    }));

    data.forEach((row) => {
      const date = new Date(row.DataWystawienia);
      if (date.getFullYear() === selectedYear) {
        const m = date.getMonth();

        // KRAJ = K_15 + K_17 + K_19 + K_10
        totals[m].Kraj +=
          safeParseValue(row.K_15) +
          safeParseValue(row.K_17) +
          safeParseValue(row.K_19) +
          safeParseValue(row.K_10);

        // UE = K_21 + K_11
        totals[m].UE += safeParseValue(row.K_21) + safeParseValue(row.K_11);

        // EKSPORT = K_22
        totals[m].Eksport += safeParseValue(row.K_22);
      }
    });

    return totals;
  }, [data, selectedYear]);

  const chartData = {
    labels: months,
    datasets: Object.keys(COLORS).map((type) => ({
      label: type,
      data: monthlyTotals.map((month) => parseFloat((month[type] || 0).toFixed(2))),
      backgroundColor: COLORS[type],
    })),
  };

  return (
    <div style={{ width: "100%", margin: "20px auto", textAlign: "center" }}>
      <ChartTitle>
        Sprzedaż wg pochodzenia transakcji w czasie
        <InfoIconWrapper data-tooltip-id="transactions-tooltip">
          <Info />
        </InfoIconWrapper>
      </ChartTitle>

      <InfoTooltip
        id="transactions-tooltip"
        place="right"
        style={{ fontSize: "12px", fontFamily: "Arial, sans-serif", color: "#fff" }}
        content={
          <>
            <strong>Sprzedaż wg pochodzenia</strong><br/>
            Wykres pokazuje wartości miesięczne dla kategorii:<br/>
            <em>Kraj, UE, Eksport</em> w wybranym roku.
          </>
        }
      />

      <div
        style={{
          marginBottom: "20px",
          display: "flex",
          justifyContent: "center",
          gap: "12px",
          alignItems: "center"
        }}
      >
        <label htmlFor="year" style={{ fontWeight: 500 }}>Rok:</label>
        <select
          id="year"
          value={selectedYear || ""}
          onChange={(e) => setSelectedYear(parseInt(e.target.value))}
          style={{
            padding: "8px 12px",
            fontSize: "15px",
            border: "1px solid #ccc",
            borderRadius: "4px",
            cursor: "pointer"
          }}
        >
          {years.map((year) => (
            <option key={year} value={year}>
              {year}
            </option>
          ))}
        </select>
      </div>

      <div style={{ width: "100%", maxWidth: "900px", height: "580px", margin: "0 auto" }}>
        <Bar
          data={chartData}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: { position: "bottom" },
              tooltip: {
                backgroundColor: "#333",
                titleColor: "#fff",
                bodyColor: "#fff",
                callbacks: {
                  label: (context) => `${context.dataset.label}: ${formatNumber(context.raw)}`
                },
              },
            },
            scales: {
              x: { stacked: true },
              y: {
                stacked: true,
                title: {
                  display: true,
                  text: "Kwota",
                  color: "#666",
                  font: { size: 12, weight: "bold" }
                },
                ticks: {
                  callback: (value) => formatNumber(value)
                }
              },
            },
          }}
        />
      </div>
    </div>
  );
};

export default TransactionStructureOverTime;
